import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    MessageCircle, Sparkles, Lock, Heart, X, Clock, Zap, Eye,
} from "lucide-react";
import { api, photoUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePaywall } from "@/contexts/PaywallContext";
import { toast } from "sonner";
import VerifiedBadge from "@/components/VerifiedBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toAbsolute(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/photos/")) return photoUrl(url.replace("/api/photos/", ""));
    return `${BACKEND_URL}${url}`;
}

function timeAgo(iso) {
    if (!iso) return "";
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
}

const TABS = [
    { key: "connections", label: "Connections" },
    { key: "requests", label: "Requests" },
    { key: "sent", label: "Sent" },
];

export default function ChatTab() {
    const { user } = useAuth();
    const { openPaywall, status } = usePaywall();
    const navigate = useNavigate();
    const [active, setActive] = useState("connections");
    const [matches, setMatches] = useState([]);
    const [likes, setLikes] = useState({ count: 0, premium: false, profiles: [] });
    const [sent, setSent] = useState({ count: 0, profiles: [] });
    const [loading, setLoading] = useState(true);

    const isPremium = !!status?.premium;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [m, l, s] = await Promise.all([
                api.get("/matches"),
                api.get("/likes/received"),
                api.get("/likes/sent"),
            ]);
            setMatches(m.data.matches || []);
            setLikes(l.data || { count: 0, premium: false, profiles: [] });
            setSent(s.data || { count: 0, profiles: [] });
        } catch (e) {
            toast.error("Couldn't load chat data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, [load]);

    const requestsCount = likes.count || 0;
    const sentCount = sent.count || 0;
    const connectionsCount = matches.length;
    const totalUnread = matches.reduce((n, m) => n + (m.unread_count || 0), 0);

    return (
        <div className="mx-auto max-w-xl px-5 pt-8 pb-24" data-testid="chat-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1">
                        <Sparkles className="h-3 w-3" strokeWidth={3} /> your inbox
                    </div>
                    <div className="font-display text-4xl font-black tracking-tighter leading-tight">
                        say <span className="font-fancy text-gradient-purple">hey.</span>
                    </div>
                </div>
                {!isPremium && (
                    <button
                        onClick={() => openPaywall({ reason: "chat_limit" })}
                        data-testid="chat-tab-unlock-btn"
                        className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-[10px] font-black uppercase tracking-widest lift flex items-center gap-1.5"
                    >
                        <Lock className="h-3 w-3" strokeWidth={3} />
                        Unlock
                    </button>
                )}
            </div>

            {/* Locked banner for free users */}
            {!isPremium && (
                <motion.button
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => openPaywall({ reason: "chat_limit" })}
                    data-testid="chat-locked-banner"
                    className="mt-4 w-full text-left rounded-3xl border-2 border-foreground bg-primary text-primary-foreground p-4 relative overflow-hidden lift"
                >
                    <div className="blob" style={{ width: 160, height: 160, top: -50, right: -30, background: "white", opacity: 0.15 }} />
                    <div className="relative flex items-center gap-3">
                        <div className="h-11 w-11 rounded-2xl bg-white/20 grid place-items-center flex-shrink-0">
                            <Zap className="h-5 w-5" fill="currentColor" strokeWidth={0} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-black text-base leading-tight">
                                Chat is Premium 💬
                            </div>
                            <div className="text-xs opacity-90">
                                Send messages to any connection for ₹{status?.price_inr ?? 99}/mo · Cancel anytime
                            </div>
                        </div>
                    </div>
                </motion.button>
            )}

            {/* Segmented tabs */}
            <div className="mt-5 flex items-center gap-1 rounded-full bg-accent p-1" data-testid="chat-tab-segmented">
                {TABS.map((t) => {
                    const isActive = active === t.key;
                    const badge =
                        t.key === "connections" ? totalUnread :
                            t.key === "requests" ? requestsCount :
                                t.key === "sent" ? sentCount : 0;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setActive(t.key)}
                            data-testid={`chat-seg-${t.key}`}
                            className={`flex-1 h-9 rounded-full text-xs font-black uppercase tracking-wider relative transition ${
                                isActive
                                    ? "bg-primary text-primary-foreground"
                                    : "text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            <span>{t.label}</span>
                            {badge > 0 && (
                                <span
                                    className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full px-1 text-[10px] font-black ${
                                        isActive ? "bg-white text-primary" : "bg-primary text-primary-foreground"
                                    }`}
                                >
                                    {badge > 99 ? "99+" : badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Content */}
            <div className="mt-5">
                <AnimatePresence mode="wait">
                    {active === "connections" && (
                        <motion.div
                            key="connections"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15 }}
                        >
                            <ConnectionsList
                                matches={matches}
                                loading={loading}
                                isPremium={isPremium}
                                onOpenChat={(m) => navigate(`/app/chats/${m.match_id}`)}
                                onPaywall={() => openPaywall({ reason: "chat_limit" })}
                            />
                        </motion.div>
                    )}
                    {active === "requests" && (
                        <motion.div
                            key="requests"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15 }}
                        >
                            <RequestsList
                                likes={likes}
                                loading={loading}
                                onAccepted={load}
                                onPaywall={() => openPaywall({
                                    reason: "generic",
                                    message: `Unlock ${likes.count} people who liked you 👀`,
                                })}
                            />
                        </motion.div>
                    )}
                    {active === "sent" && (
                        <motion.div
                            key="sent"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15 }}
                        >
                            <SentList sent={sent} loading={loading} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

// ============ Connections (mutual matches) ============
function ConnectionsList({ matches, loading, isPremium, onOpenChat, onPaywall }) {
    if (loading && matches.length === 0) {
        return <div className="text-sm text-muted-foreground text-center py-10">Loading…</div>;
    }
    if (matches.length === 0) {
        return (
            <div className="rounded-3xl border-2 border-dashed border-border bg-card p-8 text-center" data-testid="connections-empty">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-accent grid place-items-center text-accent-foreground">
                    <MessageCircle className="h-7 w-7" strokeWidth={2.5} />
                </div>
                <div className="mt-4 font-display text-xl font-bold">No connections yet</div>
                <p className="text-sm text-muted-foreground mt-1">
                    Mutual likes appear here. Keep swiping!
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-2" data-testid="connections-list">
            {matches.map((m, i) => {
                const u = m.user;
                const avatar = u.picture || (u.photo_urls?.[0] ? toAbsolute(u.photo_urls[0]) : null);
                return (
                    <motion.button
                        key={m.match_id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.02 * i }}
                        onClick={() => (isPremium ? onOpenChat(m) : onPaywall())}
                        data-testid={`connection-row-${m.match_id}`}
                        className="w-full text-left flex items-center gap-3 rounded-3xl border-2 border-foreground bg-card p-3 hover:bg-accent/40 lift relative"
                    >
                        <div className="relative flex-shrink-0">
                            <div className="h-14 w-14 rounded-full overflow-hidden bg-accent grid place-items-center text-accent-foreground font-black">
                                {avatar ? (
                                    <img src={avatar} alt={u.name} className="h-full w-full object-cover" />
                                ) : (
                                    (u.name || "?").charAt(0).toUpperCase()
                                )}
                            </div>
                            {m.unread_count > 0 && (
                                <span
                                    data-testid={`connection-unread-${m.match_id}`}
                                    className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-black grid place-items-center border-2 border-card"
                                >
                                    {m.unread_count}
                                </span>
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 justify-between">
                                <div className="font-display font-bold truncate flex items-center gap-1">
                                    {u.name}
                                    <VerifiedBadge verified={u.verified} size="xs" />
                                </div>
                                <div className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                                    {timeAgo(m.last_message?.created_at || m.created_at)}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                                <Sparkles className="h-3 w-3 text-primary flex-shrink-0" strokeWidth={3} />
                                <span className="text-primary font-semibold">{u.match_percent}%</span>
                                <span className="text-muted-foreground mx-1">·</span>
                                <span className={`truncate ${m.unread_count > 0 ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                                    {isPremium ? (m.last_message?.body || "Say hi 👋") : "Locked · unlock chat"}
                                </span>
                            </div>
                        </div>
                        {!isPremium && (
                            <div className="h-8 w-8 rounded-full bg-primary/10 grid place-items-center flex-shrink-0">
                                <Lock className="h-4 w-4 text-primary" strokeWidth={2.8} />
                            </div>
                        )}
                    </motion.button>
                );
            })}
        </div>
    );
}

// ============ Requests (people who liked me) ============
function RequestsList({ likes, loading, onAccepted, onPaywall }) {
    const [busyId, setBusyId] = useState(null);

    const accept = async (profile) => {
        if (busyId) return;
        setBusyId(profile.user_id);
        try {
            const { data } = await api.post("/swipe", {
                target_user_id: profile.user_id,
                direction: "like",
            });
            if (data.matched) {
                toast.success(`It's a match with ${profile.name || "them"}! 🎉`);
            } else {
                toast(`Accepted 💜`);
            }
            onAccepted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Couldn't accept");
        } finally {
            setBusyId(null);
        }
    };

    const reject = async (profile) => {
        if (busyId) return;
        setBusyId(profile.user_id);
        try {
            await api.post("/likes/reject", { user_id: profile.user_id });
            toast("Skipped");
            onAccepted?.();
        } catch (e) {
            toast.error("Couldn't skip");
        } finally {
            setBusyId(null);
        }
    };

    if (loading && likes.profiles.length === 0) {
        return <div className="text-sm text-muted-foreground text-center py-10">Loading…</div>;
    }
    if (likes.count === 0) {
        return (
            <div className="rounded-3xl border-2 border-dashed border-border bg-card p-8 text-center" data-testid="requests-empty">
                <div className="text-4xl mb-3">🌱</div>
                <div className="font-display text-xl font-bold">No requests yet</div>
                <p className="text-sm text-muted-foreground mt-1">
                    When someone likes your vibe, they'll appear here.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-2" data-testid="requests-list">
            {likes.profiles.map((p, i) => {
                const blurred = !!p.blurred;
                const avatar = !blurred && (p.picture || (p.photo_urls?.[0] ? toAbsolute(p.photo_urls[0]) : null));
                return (
                    <motion.div
                        key={p.user_id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.02 * i }}
                        data-testid={`request-row-${i}`}
                        className="flex items-center gap-3 rounded-3xl border-2 border-foreground bg-card p-3"
                    >
                        <div className="relative flex-shrink-0">
                            <div className={`h-14 w-14 rounded-full overflow-hidden grid place-items-center text-white text-lg font-black ${
                                blurred ? "bg-gradient-to-br from-primary/70 via-fuchsia-400/60 to-orange-300/70" : "bg-accent text-accent-foreground"
                            }`}>
                                {blurred ? (
                                    <div className="absolute inset-0 backdrop-blur-md grid place-items-center">
                                        <Eye className="h-5 w-5 text-white/90" strokeWidth={2.8} />
                                    </div>
                                ) : avatar ? (
                                    <img src={avatar} alt={p.name} className="h-full w-full object-cover" />
                                ) : (
                                    <span className="text-accent-foreground">{(p.name || "?").charAt(0).toUpperCase()}</span>
                                )}
                            </div>
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 justify-between">
                                <div className="font-display font-bold truncate">
                                    {blurred ? (
                                        <span className="text-muted-foreground">
                                            {p.name_initial}
                                            <span className="mx-0.5">•</span>
                                            <span className="text-[10px] font-black uppercase tracking-widest text-primary">hidden</span>
                                        </span>
                                    ) : (
                                        <>{p.name}{p.age ? `, ${p.age}` : ""}</>
                                    )}
                                </div>
                                <div className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                                    {timeAgo(p.liked_at)}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 text-xs mt-0.5">
                                <Sparkles className="h-3 w-3 text-primary" strokeWidth={3} />
                                <span className="text-primary font-semibold">{p.match_percent}%</span>
                                <span className="text-muted-foreground mx-1">·</span>
                                <span className="text-muted-foreground truncate">
                                    {p.city || (blurred && p.age_band ? `age ${p.age_band}` : "somewhere cool")}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                                onClick={() => reject(p)}
                                disabled={!!busyId}
                                data-testid={`request-reject-btn-${i}`}
                                className="h-9 w-9 rounded-full bg-accent text-accent-foreground grid place-items-center lift disabled:opacity-50 hover:bg-destructive/10"
                                aria-label="Skip"
                            >
                                <X className="h-4 w-4" strokeWidth={2.8} />
                            </button>
                            {blurred ? (
                                <button
                                    onClick={onPaywall}
                                    data-testid={`request-unlock-btn-${i}`}
                                    className="h-9 rounded-full bg-primary text-primary-foreground px-3 text-[10px] font-black uppercase tracking-widest lift flex items-center gap-1"
                                >
                                    <Lock className="h-3 w-3" strokeWidth={3} /> Unlock
                                </button>
                            ) : (
                                <button
                                    onClick={() => accept(p)}
                                    disabled={!!busyId}
                                    data-testid={`request-accept-btn-${i}`}
                                    className="h-9 rounded-full bg-primary text-primary-foreground px-4 text-xs font-black uppercase tracking-widest lift disabled:opacity-50 flex items-center gap-1"
                                >
                                    <Heart className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
                                    Accept
                                </button>
                            )}
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
}

// ============ Sent (I liked, they haven't reciprocated) ============
function SentList({ sent, loading }) {
    if (loading && sent.profiles.length === 0) {
        return <div className="text-sm text-muted-foreground text-center py-10">Loading…</div>;
    }
    if (sent.count === 0) {
        return (
            <div className="rounded-3xl border-2 border-dashed border-border bg-card p-8 text-center" data-testid="sent-empty">
                <div className="text-4xl mb-3">📤</div>
                <div className="font-display text-xl font-bold">No pending sent likes</div>
                <p className="text-sm text-muted-foreground mt-1">
                    People you like will appear here until they like you back.
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-2" data-testid="sent-list">
            {sent.profiles.map((p, i) => {
                const avatar = p.picture || (p.photo_urls?.[0] ? toAbsolute(p.photo_urls[0]) : null);
                return (
                    <motion.div
                        key={p.user_id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.02 * i }}
                        data-testid={`sent-row-${i}`}
                        className="flex items-center gap-3 rounded-3xl border border-border bg-card p-3"
                    >
                        <div className="h-14 w-14 rounded-full overflow-hidden bg-accent grid place-items-center text-accent-foreground font-black flex-shrink-0">
                            {avatar ? (
                                <img src={avatar} alt={p.name} className="h-full w-full object-cover" />
                            ) : (
                                (p.name || "?").charAt(0).toUpperCase()
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 justify-between">
                                <div className="font-display font-bold truncate">
                                    {p.name}{p.age ? `, ${p.age}` : ""}
                                </div>
                                <div className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                                    {timeAgo(p.liked_at)}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 text-xs mt-0.5">
                                <Sparkles className="h-3 w-3 text-primary" strokeWidth={3} />
                                <span className="text-primary font-semibold">{p.match_percent}%</span>
                                <span className="text-muted-foreground mx-1">·</span>
                                <span className="text-muted-foreground truncate flex items-center gap-1">
                                    <Clock className="h-3 w-3" strokeWidth={2.8} /> waiting for reply
                                </span>
                            </div>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
}
